import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { invokeFunction } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FileText, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { removeOutlineSectionByChapterId, removeOutlineTopicByNodeId } from "@/lib/outlineMutations";

interface SkillChapter {
  id: string;
  title: string;
  order_index: number;
}

interface SkillNode {
  id: string;
  chapter_id: string | null;
  title: string;
  position_index: number;
}

interface CourseOutlineSidebarProps {
  courseId: string;
  /** When present and no skill tree exists, we create the tree from this outline (source of truth). */
  creationOutline?: { title?: string; description?: string; sections?: { title?: string; description?: string; topics?: string[] }[]; modules?: { title?: string; description?: string; topics?: string[] }[] } | null;
  /** After outline/tree mutations (delete chapter/node). */
  onTreeUpdated?: () => void;
}

/** Compact course outline (chapters → nodes) for the left sidebar. Click → lesson page. */
const CourseOutlineSidebar = ({ courseId, creationOutline, onTreeUpdated }: CourseOutlineSidebarProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [chapters, setChapters] = useState<SkillChapter[]>([]);
  const [nodes, setNodes] = useState<SkillNode[]>([]);
  const [hasTree, setHasTree] = useState(false);
  const migrationToastShownRef = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "chapter"; chapterId: string; title: string }
    | { kind: "node"; nodeId: string; title: string }
    | null
  >(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const res =
        deleteTarget.kind === "chapter"
          ? await removeOutlineSectionByChapterId(courseId, deleteTarget.chapterId)
          : await removeOutlineTopicByNodeId(courseId, deleteTarget.nodeId);
      if (res.error) {
        toast({ variant: "destructive", title: "Could not delete", description: res.error });
      } else {
        toast({
          title: deleteTarget.kind === "chapter" ? "Chapter deleted" : "Node removed",
          description: "Outline and skill tree were updated.",
        });
        setDeleteTarget(null);
        await fetchTree();
        onTreeUpdated?.();
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const fetchTree = async () => {
    const { data, error } = await invokeFunction("get-skill-tree", {
      body: { courseId },
    });
    if (error) {
      console.warn("[CourseOutlineSidebar] get-skill-tree error:", error);
      setChapters([]);
      setNodes([]);
      setHasTree(false);
      return null;
    }
    const chList = (data?.chapters ?? []).sort((a: SkillChapter, b: SkillChapter) => a.order_index - b.order_index);
    const nodeList = data?.nodes ?? [];
    setChapters(chList);
    setNodes(nodeList);
    setHasTree(!!data?.tree);
    if ((data as { chaptersFetchFailed?: boolean })?.chaptersFetchFailed && !migrationToastShownRef.current) {
      migrationToastShownRef.current = true;
      toast({
        variant: "destructive",
        title: "Outline table missing",
        description: "Run the SQL in supabase/migrations/RUN_ME_skill_tree_chapters.sql in Supabase Dashboard → SQL Editor, then refresh.",
      });
    }
    return data;
  };

  const creationOutlineRef = useRef(creationOutline);
  creationOutlineRef.current = creationOutline;
  const outlineKey = creationOutline
    ? JSON.stringify(creationOutline.sections ?? creationOutline.modules ?? [])
    : "";
  const outlineKeyRef = useRef<string>("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await fetchTree();
      if (cancelled) return;
      const outline = creationOutlineRef.current;
      const sections = outline?.sections ?? outline?.modules;
      const hasOutlineSections = sections && Array.isArray(sections) && sections.length > 0;
      const treeEmpty = !data?.tree || ((data?.chapters?.length ?? 0) === 0 && (data?.nodes?.length ?? 0) === 0);
      const alreadySyncedThisOutline = outlineKeyRef.current === outlineKey && !treeEmpty;
      if (!cancelled && hasOutlineSections && treeEmpty && !alreadySyncedThisOutline) {
        setSyncing(true);
        const { data: saveData, error: saveErr } = await invokeFunction("save-addie-outline", {
          body: { project_id: courseId, creation_outline: outline },
        });
        if (!cancelled && !saveErr) {
          outlineKeyRef.current = outlineKey;
          const syncError = (saveData as { syncError?: string })?.syncError;
          if (syncError) {
            toast({
              variant: "destructive",
              title: "Skill tree sync failed",
              description: syncError.includes("Run migration") ? "The skill_tree_chapters table may be missing. Run the SQL in supabase/migrations/RUN_ME_skill_tree_chapters.sql in Supabase Dashboard → SQL Editor, then refresh." : syncError,
            });
          }
          await fetchTree();
        } else if (!cancelled && saveErr) {
          toast({
            variant: "destructive",
            title: "Could not create skill tree",
            description: saveErr.message || "Outline is saved; try refreshing.",
          });
        }
        if (!cancelled) setSyncing(false);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [courseId, outlineKey]);

  const nodesByChapter = new Map<string | null, SkillNode[]>();
  nodes.forEach((n) => {
    const key = n.chapter_id ?? null;
    if (!nodesByChapter.has(key)) nodesByChapter.set(key, []);
    nodesByChapter.get(key)!.push(n);
  });
  nodesByChapter.forEach((arr) => arr.sort((a, b) => a.position_index - b.position_index));

  const handleOpenNode = (nodeId: string) => {
    navigate(`/project/${courseId}/node/${nodeId}/lesson`, { state: { fromMaterials: true } });
  };

  const handleOpenChapter = (chapterId: string) => {
    const first = nodesByChapter.get(chapterId)?.[0];
    if (first) navigate(`/project/${courseId}/node/${first.id}/lesson`, { state: { fromMaterials: true } });
    else toast({ title: "No nodes", description: "This chapter has no nodes yet." });
  };

  if (loading || syncing) {
    return (
      <div className="px-2 py-3 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>{syncing ? "Building outline…" : "Loading outline…"}</span>
      </div>
    );
  }

  if (!hasTree || (chapters.length === 0 && nodes.length === 0)) {
    const hasOutline = (creationOutline?.sections?.length ?? 0) > 0 || (creationOutline?.modules?.length ?? 0) > 0;
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        {hasOutline
          ? "Building skill tree from outline…"
          : "Add a course outline first (e.g. from course creation), then the skill tree will be created from it."}
      </div>
    );
  }

  const uncategorizedNodes = nodesByChapter.get(null) ?? [];

  return (
    <div className="mt-1 space-y-0.5 border-t pt-2">
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && !deleteBusy && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTarget?.kind === "chapter" ? "Delete chapter?" : "Delete this node?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "chapter" ? (
                <>
                  Remove &quot;{deleteTarget.title}&quot; from the outline and delete the chapter and all nodes under it.
                  This cannot be undone.
                </>
              ) : deleteTarget?.kind === "node" ? (
                <>
                  Remove &quot;{deleteTarget.title}&quot; from the outline (when listed) and delete its skill node and
                  generated content. This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteBusy}
              onClick={(e) => {
                e.preventDefault();
                void runDelete();
              }}
            >
              {deleteBusy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="px-2 pb-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Course outline</p>
      <nav className="grid gap-0.5">
        {chapters.map((ch) => {
          const chapterNodes = nodesByChapter.get(ch.id) ?? [];
          return (
            <div key={ch.id} className="group/ch">
              <div className="flex items-stretch gap-0.5 pr-1">
                <button
                  type="button"
                  onClick={() => handleOpenChapter(ch.id)}
                  className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 text-left rounded-md hover:bg-muted/80 text-sm font-medium transition-colors"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{ch.title}</span>
                  <ChevronRight className="h-3 w-3 shrink-0 ml-auto text-muted-foreground opacity-70" />
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 opacity-70 group-hover/ch:opacity-100"
                  title="Delete chapter"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget({ kind: "chapter", chapterId: ch.id, title: ch.title });
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {chapterNodes.map((node) => (
                <div key={node.id} className="flex items-stretch gap-0.5 pr-1 group/node">
                  <button
                    type="button"
                    onClick={() => handleOpenNode(node.id)}
                    className="flex-1 min-w-0 flex items-center gap-1.5 pl-5 pr-2 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-md transition-colors text-left"
                  >
                    <span className="text-muted-foreground">•</span>
                    <span className="truncate">{node.title}</span>
                    <ChevronRight className="h-3 w-3 shrink-0 ml-auto opacity-50" />
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover/node:opacity-100"
                    title="Delete node"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({ kind: "node", nodeId: node.id, title: node.title });
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          );
        })}
        {uncategorizedNodes.length > 0 && (
          <>
            <p className="px-2 pt-2 pb-0.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Uncategorized</p>
            {uncategorizedNodes.map((node) => (
              <div key={node.id} className="flex items-stretch gap-0.5 pr-1 group/unc">
                <button
                  type="button"
                  onClick={() => handleOpenNode(node.id)}
                  className="flex-1 min-w-0 flex items-center gap-1.5 pl-5 pr-2 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-md transition-colors text-left"
                >
                  <span className="text-muted-foreground">•</span>
                  <span className="truncate">{node.title}</span>
                  <ChevronRight className="h-3 w-3 shrink-0 ml-auto opacity-50" />
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover/unc:opacity-100"
                  title="Delete node"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget({ kind: "node", nodeId: node.id, title: node.title });
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </>
        )}
      </nav>
    </div>
  );
};

export default CourseOutlineSidebar;
